import { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Tag, List, Avatar, Switch, Space, Select, Badge, Spin } from 'antd';
import {
  CarOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ReloadOutlined,
  HeatMapOutlined,
} from '@ant-design/icons';
import { YMaps, Map, Placemark, Clusterer } from '@pbe/react-yandex-maps';
import { apiClient } from '../../lib/api-client';
import { io } from 'socket.io-client';

interface DriverLocation {
  id: number;
  fullName: string;
  phone: string;
  lat: number;
  lng: number;
  status: 'ONLINE' | 'OFFLINE' | 'BUSY';
  vehicleInfo?: string;
  lastUpdated: string;
}

interface ActiveTrip {
  id: number;
  tripType: string;
  status: string;
  clientName: string;
  driverName: string;
  pickupLat: number;
  pickupLng: number;
  destinationLat: number;
  destinationLng: number;
  pickupAddress: string;
  destinationAddress: string;
  currentLat?: number;
  currentLng?: number;
}

interface RegionStats {
  id: number;
  name: string;
  driversOnline: number;
  activeTrips: number;
  pendingRequests: number;
}

interface LiveStats {
  totalOnlineDrivers: number;
  totalActiveTrips: number;
  totalPendingRequests: number;
  regions: RegionStats[];
}

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida',
  INTERREGION_TAXI: 'Shaharlararo',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk',
};

const tripStatusLabels: Record<string, string> = {
  REQUESTED: 'So\'ralgan',
  ACCEPTED: 'Qabul qilingan',
  DRIVER_ARRIVED: 'Haydovchi yetib keldi',
  PICKED_UP: 'Olib ketildi',
  ON_THE_WAY: 'Yo\'lda',
  IN_PROGRESS: 'Jarayonda',
};

const statusColors: Record<string, string> = {
  REQUESTED: 'orange',
  ACCEPTED: 'blue',
  DRIVER_ARRIVED: 'purple',
  PICKED_UP: 'cyan',
  ON_THE_WAY: 'green',
  IN_PROGRESS: 'green',
};

// Uzbekistan center coordinates
const DEFAULT_CENTER: [number, number] = [41.3111, 69.2797];
const DEFAULT_ZOOM = 6;

export const LiveMap = () => {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<DriverLocation[]>([]);
  const [activeTrips, setActiveTrips] = useState<ActiveTrip[]>([]);
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [showDrivers, setShowDrivers] = useState(true);
  const [showTrips, setShowTrips] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);

  const fetchData = useCallback(async () => {
    try {
      const [driversRes, tripsRes, statsRes] = await Promise.all([
        apiClient.get('/admin/live/drivers', {
          params: { regionId: selectedRegion },
        }),
        apiClient.get('/admin/live/trips', {
          params: { regionId: selectedRegion },
        }),
        apiClient.get('/admin/live/stats'),
      ]);

      setDrivers(driversRes.data);
      setActiveTrips(tripsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch live data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedRegion]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Socket connection for real-time updates
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const newSocket = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem('token'),
      },
    });

    newSocket.on('connect', () => {
      console.log('Connected to live updates');
      newSocket.emit('admin:join');
    });

    newSocket.on('driver:location', (data: DriverLocation) => {
      setDrivers(prev => {
        const index = prev.findIndex(d => d.id === data.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = data;
          return updated;
        }
        return [...prev, data];
      });
    });

    newSocket.on('driver:offline', (driverId: number) => {
      setDrivers(prev => prev.filter(d => d.id !== driverId));
    });

    newSocket.on('trip:update', (trip: ActiveTrip) => {
      setActiveTrips(prev => {
        const index = prev.findIndex(t => t.id === trip.id && t.tripType === trip.tripType);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = trip;
          return updated;
        }
        return [...prev, trip];
      });
    });

    newSocket.on('trip:completed', (data: { id: number; tripType: string }) => {
      setActiveTrips(prev =>
        prev.filter(t => !(t.id === data.id && t.tripType === data.tripType))
      );
    });

    newSocket.on('stats:update', (newStats: LiveStats) => {
      setStats(newStats);
    });

    return () => {
      newSocket.emit('admin:leave');
      newSocket.disconnect();
    };
  }, []);

  // Periodic refresh as fallback
  useEffect(() => {
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  const getDriverMarkerColor = (status: string) => {
    switch (status) {
      case 'ONLINE':
        return '#27AE60';
      case 'BUSY':
        return '#F39C12';
      default:
        return '#7F8C8D';
    }
  };

  const handleRegionSelect = (regionId: number | null) => {
    setSelectedRegion(regionId);
    if (regionId && stats) {
      const region = stats.regions.find(r => r.id === regionId);
      if (region) {
        // Focus on region (would need region center coordinates)
        setMapZoom(10);
      }
    } else {
      setMapCenter(DEFAULT_CENTER);
      setMapZoom(DEFAULT_ZOOM);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, height: 'calc(100vh - 64px)' }}>
      <Row gutter={16} style={{ height: '100%' }}>
        {/* Map Section */}
        <Col span={18} style={{ height: '100%' }}>
          <Card
            title={
              <Space>
                <EnvironmentOutlined />
                <span>Jonli xarita</span>
                <Badge
                  status="processing"
                  text={
                    <span style={{ fontSize: 12, color: '#7F8C8D' }}>
                      Real vaqt
                    </span>
                  }
                />
              </Space>
            }
            extra={
              <Space>
                <Switch
                  checkedChildren={<CarOutlined />}
                  unCheckedChildren={<CarOutlined />}
                  checked={showDrivers}
                  onChange={setShowDrivers}
                />
                <span style={{ fontSize: 12 }}>Haydovchilar</span>

                <Switch
                  checkedChildren={<EnvironmentOutlined />}
                  unCheckedChildren={<EnvironmentOutlined />}
                  checked={showTrips}
                  onChange={setShowTrips}
                />
                <span style={{ fontSize: 12 }}>Sayohatlar</span>

                <Switch
                  checkedChildren={<HeatMapOutlined />}
                  unCheckedChildren={<HeatMapOutlined />}
                  checked={showHeatmap}
                  onChange={setShowHeatmap}
                />
                <span style={{ fontSize: 12 }}>Issiqlik xaritasi</span>

                <Select
                  placeholder="Viloyat"
                  allowClear
                  style={{ width: 150 }}
                  value={selectedRegion}
                  onChange={handleRegionSelect}
                  options={stats?.regions.map(r => ({
                    value: r.id,
                    label: r.name,
                  })) || []}
                />
              </Space>
            }
            styles={{ body: { padding: 0, height: 'calc(100% - 57px)' } }}
            style={{ height: '100%' }}
          >
            <YMaps query={{ apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY || '' }}>
              <Map
                defaultState={{ center: mapCenter, zoom: mapZoom }}
                state={{ center: mapCenter, zoom: mapZoom }}
                width="100%"
                height="100%"
                modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
              >
                {/* Driver markers */}
                {showDrivers && (
                  <Clusterer
                    options={{
                      preset: 'islands#greenClusterIcons',
                      groupByCoordinates: false,
                    }}
                  >
                    {drivers.map((driver) => (
                      <Placemark
                        key={`driver-${driver.id}`}
                        geometry={[driver.lat, driver.lng]}
                        options={{
                          preset: 'islands#circleIcon',
                          iconColor: getDriverMarkerColor(driver.status),
                        }}
                        properties={{
                          hintContent: driver.fullName || 'Haydovchi',
                          balloonContentHeader: `<strong>${driver.fullName || 'Haydovchi'}</strong>`,
                          balloonContentBody: `
                            <div style="padding: 8px;">
                              <div>Telefon: ${driver.phone}</div>
                              <div>Holat: ${driver.status === 'ONLINE' ? 'Online' : driver.status === 'BUSY' ? 'Band' : 'Offline'}</div>
                              ${driver.vehicleInfo ? `<div>Mashina: ${driver.vehicleInfo}</div>` : ''}
                              <div style="color: #7F8C8D; font-size: 11px; margin-top: 4px;">
                                Oxirgi yangilanish: ${new Date(driver.lastUpdated).toLocaleTimeString('uz-UZ')}
                              </div>
                            </div>
                          `,
                        }}
                      />
                    ))}
                  </Clusterer>
                )}

                {/* Active trip markers */}
                {showTrips && activeTrips.map((trip) => (
                  <Placemark
                    key={`trip-${trip.tripType}-${trip.id}`}
                    geometry={[
                      trip.currentLat || trip.pickupLat,
                      trip.currentLng || trip.pickupLng,
                    ]}
                    options={{
                      preset: 'islands#redAutoIcon',
                    }}
                    properties={{
                      hintContent: `Sayohat #${trip.id}`,
                      balloonContentHeader: `<strong>Sayohat #${trip.id}</strong>`,
                      balloonContentBody: `
                        <div style="padding: 8px;">
                          <div>Turi: ${tripTypeLabels[trip.tripType] || trip.tripType}</div>
                          <div>Holat: ${tripStatusLabels[trip.status] || trip.status}</div>
                          <div>Mijoz: ${trip.clientName || 'Ismi yo\'q'}</div>
                          <div>Haydovchi: ${trip.driverName || 'Tayinlanmagan'}</div>
                          <hr style="margin: 8px 0;" />
                          <div style="font-size: 11px;">
                            <div>Olish: ${trip.pickupAddress}</div>
                            <div>Borish: ${trip.destinationAddress}</div>
                          </div>
                        </div>
                      `,
                    }}
                  />
                ))}
              </Map>
            </YMaps>
          </Card>
        </Col>

        {/* Stats & Lists Section */}
        <Col span={6} style={{ height: '100%', overflow: 'auto' }}>
          {/* Quick Stats */}
          <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="Online haydovchilar"
                  value={stats?.totalOnlineDrivers || 0}
                  prefix={<CarOutlined style={{ color: '#27AE60' }} />}
                  valueStyle={{ color: '#27AE60' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="Faol sayohatlar"
                  value={stats?.totalActiveTrips || 0}
                  prefix={<EnvironmentOutlined style={{ color: '#3498DB' }} />}
                  valueStyle={{ color: '#3498DB' }}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small">
                <Statistic
                  title="Kutilayotgan so'rovlar"
                  value={stats?.totalPendingRequests || 0}
                  prefix={<ReloadOutlined style={{ color: '#F39C12' }} />}
                  valueStyle={{ color: '#F39C12' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Region Stats */}
          <Card
            title="Viloyatlar statistikasi"
            size="small"
            styles={{ body: { padding: 0, maxHeight: 200, overflow: 'auto' } }}
            style={{ marginBottom: 16 }}
          >
            <List
              dataSource={stats?.regions || []}
              size="small"
              renderItem={(region) => (
                <List.Item
                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                  onClick={() => handleRegionSelect(region.id)}
                >
                  <List.Item.Meta
                    title={<span style={{ fontSize: 13 }}>{region.name}</span>}
                    description={
                      <Space size="small">
                        <Tag color="green" style={{ margin: 0 }}>
                          {region.driversOnline} haydovchi
                        </Tag>
                        <Tag color="blue" style={{ margin: 0 }}>
                          {region.activeTrips} sayohat
                        </Tag>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          {/* Active Trips List */}
          <Card
            title={
              <Space>
                <EnvironmentOutlined />
                <span>Faol sayohatlar</span>
                <Badge count={activeTrips.length} style={{ backgroundColor: '#3498DB' }} />
              </Space>
            }
            size="small"
            styles={{ body: { padding: 0, maxHeight: 300, overflow: 'auto' } }}
          >
            <List
              dataSource={activeTrips.slice(0, 20)}
              size="small"
              renderItem={(trip) => (
                <List.Item style={{ padding: '8px 12px' }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<CarOutlined />}
                        style={{ backgroundColor: statusColors[trip.status] || '#7F8C8D' }}
                        size="small"
                      />
                    }
                    title={
                      <Space>
                        <span style={{ fontSize: 12 }}>#{trip.id}</span>
                        <Tag
                          color={statusColors[trip.status] || 'default'}
                          style={{ fontSize: 10 }}
                        >
                          {tripStatusLabels[trip.status] || trip.status}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div style={{ fontSize: 11 }}>
                        <div>{trip.clientName || 'Mijoz'} → {trip.driverName || 'Haydovchi'}</div>
                        <div style={{ color: '#7F8C8D' }}>
                          {tripTypeLabels[trip.tripType] || trip.tripType}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Faol sayohatlar yo\'q' }}
            />
          </Card>

          {/* Online Drivers List */}
          <Card
            title={
              <Space>
                <UserOutlined />
                <span>Online haydovchilar</span>
                <Badge count={drivers.filter(d => d.status === 'ONLINE').length} style={{ backgroundColor: '#27AE60' }} />
              </Space>
            }
            size="small"
            styles={{ body: { padding: 0, maxHeight: 300, overflow: 'auto' } }}
            style={{ marginTop: 16 }}
          >
            <List
              dataSource={drivers.filter(d => d.status === 'ONLINE').slice(0, 20)}
              size="small"
              renderItem={(driver) => (
                <List.Item style={{ padding: '8px 12px' }}>
                  <List.Item.Meta
                    avatar={
                      <Badge
                        dot
                        color="#27AE60"
                        offset={[-2, 18]}
                      >
                        <Avatar icon={<UserOutlined />} size="small" />
                      </Badge>
                    }
                    title={
                      <span style={{ fontSize: 12 }}>{driver.fullName || 'Haydovchi'}</span>
                    }
                    description={
                      <div style={{ fontSize: 11, color: '#7F8C8D' }}>
                        {driver.phone}
                        {driver.vehicleInfo && ` • ${driver.vehicleInfo}`}
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Online haydovchilar yo\'q' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
